"use client";

import { Anchor, Box, Button } from "@mantine/core";
import { useForm } from "@mantine/form";
import { signIn } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { DEV_LOGIN_DENIED_MESSAGE } from "@/enums/auth";
import { passwordRequirements } from "../../../../lib/strapi/verification";
import { setNotification } from "../../../components/notifications";
import FieldPasswordValue from "../../profile/components/inputs/FieldPasswordValue";
import FieldTextValue from "../../profile/components/inputs/FieldTextValue";
import { emailRequirements } from "../../register/types";
import { useAuthStyles } from "../components/AuthContentWrapper";

export default function LoginInterface() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { classes: authClasses } = useAuthStyles();

  const submitHandler = async () => {
    try {
      localStorage.removeItem("progressMeta");
      setLoading(true);
      let captchaToken;
      if (process.env.NODE_ENV !== "development") {
        if (!executeRecaptcha) {
          throw new Error(
            "Please check you are connected to the internet and try again"
          );
        }
        captchaToken = await executeRecaptcha("LoginSubmit");
      }
      const res = await signIn("credentials", {
        redirect: false,
        email: form.values.email,
        password: form.values.password,
        captchaToken,
      });
      if (res?.error) {
        throw new Error(
          res.error === "fetch failed"
            ? "We couldn't contact our server. Please try again!"
            : res.error === DEV_LOGIN_DENIED_MESSAGE
              ? DEV_LOGIN_DENIED_MESSAGE
              : "Invalid email or password entered. Alternatively, you may have failed human verification. Please try again."
        );
      } else {
        setNotification("Login", false, `Welcome back!`, "success");
        const callbackUrl = res?.url
          ? new URLSearchParams(new URL(res.url).search).get("callbackUrl")
          : "/";
        router.push(callbackUrl ? callbackUrl : "/profile");
      }
    } catch (err: any) {
      setNotification("Login", false, err.message, "error");
    }
    return setLoading(false);
  };

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
    },
    validate: (values) => {
      return {
        email: emailRequirements.test(values.email) ? null : "Invalid email",
        password: passwordRequirements.every((req) =>
          req.re.test(values.password)
        )
          ? null
          : "≥8 characters with 1 upper case, number & symbol",
      };
    },
  });

  return (
    <form
      onSubmit={form.onSubmit(() => submitHandler())}
      className={authClasses.mainContent}
    >
      <Box h={80}>
        <FieldTextValue
          label="Email Address"
          editing={true}
          formProps={form.getInputProps("email")}
        />
      </Box>

      <Box h={90}>
        {/* to prevent error causing layout shift */}
        <FieldPasswordValue
          label="Password"
          editing={true}
          formProps={form.getInputProps("password")}
          disablePopover={true}
        />
        <Anchor
          href={`/forgot-password?userEmail=${form.values.email ?? ""}`}
          component={Link}
          className={authClasses.inputLink}
          tabIndex={-1}
          mt="xs"
          style={{ float: "right" }}
        >
          Forgot?
        </Anchor>
      </Box>

      <Box className={authClasses.ctaWrapper}>
        <Button
          name="Login"
          fullWidth
          type="submit"
          loading={loading}
          variant="outline"
          color="lightblue"
          className={authClasses.ctaButton}
        >
          Sign In
        </Button>
        <Anchor
          href="/register"
          component={Link}
          className={authClasses.inputLink}
          tabIndex={-1}
        >
          Register
        </Anchor>
      </Box>
    </form>
  );
}
